
import useSWRImmutable from "swr/immutable";
import authAxios from "../../helpers/authAxios";

export type ProjectList = {
 _id: string;
 project_name: string;
};

export type AssigneesList = {
 _id: string;
 profilepic: string;
 name: string;
};

export type ReportListTotal = {
 totalProjects: number;
 totalTasks: number;
 totalSubTasks: number;
 totalCL: number;
 totalSL: number;
 totalPL: number;
 totalLeaves: number;
};

export type TeamsList = {
 _id: string;
 name: string;
 description: string;
 work_schedule: string;
 manager: { _id: string; name: string; profilepic: string };
};

export type EmployeeList = {
 id: string;
 profile: {
  first_name: string;
  last_name: string;
 };
 profilepic: string;
};

export type LeaveData = {
    casual_leave?: number;
    sick_leave?: number;
    privileged_leave?: string;
    wfh?: number;
    permission?: number;
    cl_pending?: number;
    sl_pending?: number;
    pl_pending?: string;
    wfh_pending?: number;
    permission_pending?: number;
  };
  
export type EmployeeLeaveTrackerReportList = Record<string, LeaveData>;

export const getMembersByDepartment = (
 url: string
): Promise<EmployeeList[]> => {
 return authAxios.get(url).then((resp) => {
  return resp.data.data as EmployeeList[];
 });
};
export const useEmployeeList = (teamId: string | undefined) => {
 const { data, error } = useSWRImmutable<EmployeeList[]>(
  teamId ? `/departments/leaveReport/employees/list/${teamId}` : null,
  getMembersByDepartment
 );

 return {
  employeeList: data || [],
  isLoading: !error && !data,
  isError: error,
 };
};
export const getProjectList = (url: string): Promise<ProjectList[]> =>
 authAxios.get(url).then((resp) => resp.data.data);

export const getAssigneesList = (url: string): Promise<AssigneesList[]> =>
 authAxios.get(url).then((resp) => resp.data.data);

const getTeamsList = (url: string): Promise<TeamsList[]> =>
 authAxios.get(url).then((resp): TeamsList[] => {
  const result = resp.data?.result;
  return result;
 });

const getTeamDetails = (url: string): Promise<TeamsList[]> =>
 authAxios.get(url).then((resp): TeamsList[] => {
  const result = resp.data;
  return result;
 });

export const getReportList = (url: string): Promise<any> =>
 authAxios.get(url).then((resp) => resp?.data?.result);

export const getProjectReportList = (url: string): Promise<any> =>
 authAxios.get(url).then((resp) => {
  return resp.data;
 });

//To get leave report list
export const getLeaveReportList = (url: string): Promise<any> =>
 authAxios.get(url).then((resp) => {
  return resp.data;
 });

 //To get employee leave tracker list
export const getFullLeaveReportList = (url: string): Promise<any> =>
 authAxios.get(url).then((resp) => {
  return resp.data;
 });

export const getEmployeeLeaveTrackerReportList=(url:string):Promise<any>=>
    authAxios.get(url).then((resp)=>{
        return resp.data;
})

export const getReportListTotal = (url: string): Promise<ReportListTotal> =>
 authAxios.get(url).then((resp) => resp.data?.result);

export const useProjectList = () => {
 const { data, error } = useSWRImmutable(
  `/logtime/projects/list`,
  getProjectList
 );
 return {
  projectList: data || [],
  isLoading: !error && !data,
  isError: error,
 };
};

export const useAssigneesList = (projectId: string | undefined) => {
 const { data, error } = useSWRImmutable(
  projectId ? `/reports/employees/list/${projectId}` : null,
  getAssigneesList
 );
 return {
  assigneesList: data || [],
  isLoading: !error && !data,
  isError: error,
 };
};

export const useTeamsList = () => {
 const { data, error, mutate } = useSWRImmutable(
  "/departments/list",
  getTeamsList
 );

 return {
  teamList: data || [],
  isLoading: !error && !data,
  isError: error,
  mutate,
 };
};

export const useTeamDetails = (teamId: string) => {
 const { data, error, mutate } = useSWRImmutable(
  `/departments/${teamId}`,
  getTeamDetails
 );
 return {
  teamsDetails: data,
  isLoading: !error && !data,
  isError: error,
  teamMutate: mutate,
 };
};

export const useReportList = (
 projectId: string | undefined,
 assignee: string | undefined,
 startdate: string,
 enddate: string,
 groupBy: string[]
) => {
 const { data, error } = useSWRImmutable(
  `/reports/tasks/list?projectId=${projectId}&assignees=${assignee}&startdate=${startdate}&enddate=${enddate}&groupby=${groupBy}`,
  getReportList
 );

 return {
  reportList: data || [],
  isReportLoading: !error && !data,
  isError: error,
 };
};

export const useProjectReportList = (
 projectId: string | undefined,
 range: string | undefined,
 startdate: string,
 enddate: string
) => {
 const { data, error } = useSWRImmutable(
  `/reports/projects/list?projectId=${projectId}&range=${range}&startdate=${startdate}&enddate=${enddate}`,
  getProjectReportList
 );

 return {
  projectReportList: data || [],
  isProjectReportLoading: !error && !data,
  isError: error,
 };
};

export const useLeaveReportList = (
 teamId: string | undefined,
 employee: string | undefined,
 range: string | undefined,
 startdate: string,
 enddate: string
) => {
 const { data, error } = useSWRImmutable(
  `/reports/leaveReport/list?teamId=${teamId}&employee=${employee}&range=${range}&startdate=${startdate}&enddate=${enddate}`,
  getLeaveReportList
 );

 return {
  leaveReportList: data || [],
  isLeaveReportLoading: !error && !data,
  isError: error,
 };
};

export const useFullLeaveReportList = (
 teamId: string | undefined,
 employee: string | undefined,
 range: string | undefined,
 startdate: string,
 enddate: string
) => {
 const { data, error } = useSWRImmutable(
  `/leave/report?teamId=${teamId}&employee=${employee}&range=${range}&startdate=${startdate}&enddate=${enddate}`,
  getFullLeaveReportList
 );

 return {
  fullLeaveReportList: data || [],
  isLeaveReportLoading: !error && !data,
  isError: error,
 };
};


export const useEmployeeLeaveTrackerReportList=(
    teamId: string | undefined,
 employee: string | undefined,
 range: string | undefined,
 startdate: string,
 enddate: string 
)=>{
    const { data, error } = useSWRImmutable(
        `/leave/leave-summary?teamId=${teamId}&employee=${employee}&range=${range}&startdate=${startdate}&enddate=${enddate}`,
        getEmployeeLeaveTrackerReportList
       );
      
       return {
        employeeLeaveTrackerReportList: data || [],
        isLeaveReportLoading: !error && !data,
        isError: error,
       }; 
}

export const useReportListTotal = (
 projectId: string | undefined,
 assignee: string | undefined,
 startdate: string,
 enddate: string
) => {
 const { data, error } = useSWRImmutable(
  `/reports/tasks/total?projectId=${projectId}&assignees=${assignee}&startdate=${startdate}&enddate=${enddate}`,
  getReportListTotal
 );

 return {
  reportListTotal: data,
  isReportTotalLoading: !error && !data,
  isError: error,
 };
};

export const useProjectReportListTotal = (
 projectId: string | undefined,
 startdate: string,
 enddate: string
) => {
 const { data, error } = useSWRImmutable(
  `/reports/projects/total?projectId=${projectId}&startdate=${startdate}&enddate=${enddate}`,
  getReportListTotal
 );

 return {
  reportListTotal: data,
  isReportTotalLoading: !error && !data,
  isError: error,
 };
};

//Leave report total list
export const useLeaveReportListTotal = (
 teamId: string | undefined,
 employee: string | undefined,
 startdate: string,
 enddate: string
) => {
 const { data, error } = useSWRImmutable(
  `/reports/leaveReport/total?teamId=${teamId}&employee=${employee}&startdate=${startdate}&enddate=${enddate}`,
  getReportListTotal
 );

 return {
  reportListTotal: data,
  isReportTotalLoading: !error && !data,
  isError: error,
 };
};


















